/**
 * StreakDayCircle — one day cell in a weekly streak strip.
 *
 * iOS source of truth: WeeklyCalendar.tsx (home hero strip, lines 229-267) and
 * StreakCard.tsx (profile streak card). Two surfaces, two palettes:
 *   - "hero"  → on the blue sky hero: today=white bg + blue number,
 *               completed=whiteMedium bg + white check, empty=dashed white,
 *               future=faint.
 *   - "card"  → on the orange streak card: today/completed=orange bg + white
 *               check, future=pale-peach outline.
 *
 * Circle defaults to 34px (home hero strip); the profile streak card passes
 * size={24} to match iOS StreakCard.tsx (circle: {width:24,height:24}).
 */
import { FC } from "react";
import { colors } from "@/ui";
import type { DayState } from "./streakUtils";

export type StreakSurface = "hero" | "card";

export interface StreakDayCircleProps {
  label: string;
  dayNumber: number;
  state: DayState;
  surface: StreakSurface;
  /** Show the calendar day number under/in the circle (home strip only). */
  showNumber?: boolean;
  /** Circle diameter in px. iOS: 34 on the home strip, 24 on the streak card. */
  size?: number;
}

const DEFAULT_SIZE = 34;

function CheckmarkWhite({ size = 18 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden>
      <path
        d="M5 12.5l4.2 4.2L19 7"
        fill="none"
        stroke="#ffffff"
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

const StreakDayCircle: FC<StreakDayCircleProps> = ({
  label,
  dayNumber,
  state,
  surface,
  showNumber = true,
  size = DEFAULT_SIZE,
}) => {
  const isHero = surface === "hero";
  // Scale the inner check/number to the circle (iOS: 14px check in a 24px circle ≈ 0.58).
  const checkSize = Math.round(size * 0.58);

  // Resolve circle visual per (surface, state).
  let background = "transparent";
  let border = "none";
  let opacity = 1;
  let boxShadow = "none";
  let content: "check" | "number" | "none" = showNumber ? "number" : "none";
  let numberColor: string = isHero ? colors.white : colors.primary;

  if (state === "today") {
    if (isHero) {
      background = colors.white;
      numberColor = colors.blue;
      boxShadow = "0 2px 6px rgba(0,0,0,0.12)";
    } else {
      background = colors.orange;
      content = "check";
    }
  } else if (state === "completed") {
    if (isHero) {
      background = colors.whiteMedium;
      content = "check";
    } else {
      background = colors.orange;
      content = "check";
    }
  } else if (state === "empty") {
    if (isHero) {
      border = `1.5px dashed ${colors.whiteMedium}`;
      opacity = 0.45;
    } else {
      background = "rgba(255,255,255,0.45)";
      content = "none";
    }
  } else {
    // future
    if (isHero) {
      opacity = 0.6;
    } else {
      background = "rgba(255,255,255,0.45)";
      content = "none";
    }
  }

  const labelColor = isHero ? colors.white : colors.primary;

  return (
    <div
      style={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        gap: 6,
        minWidth: 0,
      }}
    >
      {/* Day label */}
      <div
        style={{
          fontSize: "var(--base-font-size-xs)",
          fontWeight: 600,
          color: labelColor,
          opacity: isHero ? 0.95 : 0.9,
        }}
      >
        {label}
      </div>
      {/* Circle */}
      <div
        style={{
          width: size,
          height: size,
          maxWidth: size,
          aspectRatio: "1 / 1",
          borderRadius: "50%",
          background,
          border,
          opacity,
          boxShadow,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {content === "check" && <CheckmarkWhite size={checkSize} />}
        {content === "number" && (
          <span
            style={{
              fontSize: "var(--base-font-size-small)",
              fontWeight: 700,
              color: numberColor,
            }}
          >
            {dayNumber}
          </span>
        )}
      </div>
    </div>
  );
};

export default StreakDayCircle;
